import type EventEmitter from 'node:events';
import {
  type SubscriptionReplayOptions,
  type ConnectivityType, type CredentialsType, type HealthcheckWorkerMessage, type OnMessageParams, type PubSubParams,
} from '../qep-system';

export type BaseApi = {
  httpClient: RestClientApi;
  subscriptionManager: SubscriptionManagerApi;
};

export type QueueableRequest = {
  method: string;
  url: string;
  params?: any;
  success: (...args: any) => any;
  failure: (...args: any) => any;
};

export type RestClientApi = {
  connectivity: ConnectivityType;
  creds: CredentialsType;
  get: (route: string, parameters?: any, retryable?: boolean, timeout?: number) => Promise<any>;
  getResponse: (route: string, parameters: any) => Promise<any>;
  // eslint-disable-next-line max-params -- The positional signature is the established REST compatibility boundary.
  post: (route: string, parameters?: any, retryable?: boolean, form?: boolean, timeout?: number) => Promise<any>;
  put: (route: string, parameters?: any, retryable?: boolean, timeout?: number) => Promise<any>;
  delete: (route: string, parameters?: any, retryable?: boolean) => Promise<any>;
  init: () => void;
  isConnected: boolean;
  queue: QueueableRequest[];
  server: string;
};

export type SubscriptionItem = {
  clientList?: Array<{
    cb?: (parameters: any) => any;
    subscribeId: string;
  }>;
  data: Record<any, any>;
  lastMessage?: {
    topic: string;
    payload: unknown;
  };
  lastResponseTime?: string | Date;
  onMessage?: (error: Error | undefined, message: any) => any;
  // Stored with the subscription so reconnect and worker health-check recovery
  // can recreate the same replay behavior.
  subscriptionOptions?: SubscriptionReplayOptions;
  subscribeTopic?: string;
  topic: string;
};
export type SubscriptionManagerApi = {
  HEALTHCHECKINTERVAL: number;
  activeSubscriptionsByTopic: Record<string, SubscriptionItem>;
  eventEmitter: EventEmitter;
  findSubscriptionByTopic: (topic: string) => SubscriptionItem | undefined;
  getAllSubscriptions: () => Record<string, SubscriptionItem>;
  getAllSubscriptionTopics: () => string[];
  internalHealthCheck: () => void;
  name: string | undefined;
  onMessage: (parameters?: OnMessageParams) => (() => void) | void;
  publish: (parameters: PubSubParams) => Promise<void>;
  requests: Record<any, unknown>;
  resubscribeToAll: () => void;
  // eslint-disable-next-line max-params -- Subscription identity, topic, payload, callback, and replay options are independent wire fields.
  subscribe: (
    topicUniqueId: string,
    topic: string,
    parameters: Record<string, unknown>,
    _cb: (error?: unknown, update?: unknown) => unknown,
    subscriptionOptions?: SubscriptionReplayOptions,
  ) => () => void;
  topicsByUniqueId: Record<string, SubscriptionItem>;
  unsubscribeFromAll: () => void;
  webSocketWorker: WebSocketType;
};

export type WebSocketType = {
  RESUBSCRIBE_TIME: number;
  checkHealthStatus: () => undefined | void;
  healthInterval: ReturnType<typeof setInterval> | undefined;
  lastHealthCheck: Date | undefined;
  onConnected: () => void;
  onPing: (message: HealthcheckWorkerMessage) => (() => void) | void;
  send: (message: string) => void;
  sendPong: () => void;
  status: string | 'connected' | 'disconnected' | 'closed';
  subscribeToMessage?: (cb: (message: OnMessageParams) => void) => () => void;
  webSocketEmitter: EventEmitter;
  onMessage: any;
  subscribeToProtocolEvent: any;
};
